'use client';

import React, { memo, useCallback, useMemo, useState } from 'react';
import { isHumanRemoved } from '#lib/leasing/leadStatus';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '../EntityView';
import { Grid } from '@/components/portal/Grid';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { supportsInventoryViewModeToggle } from '@/components/portal/inventoryViewModeCapabilities';
import { Building2, Gauge, CheckCircle2, Target } from '@/components/ui/lucide-memo';
import type { PortfolioFitDTO, PortfolioFitFilters } from '#types/types';

const translations = {
  en: {
    title: 'Portfolio fits',
    breadcrumb: 'Portfolio / Fits',
    searchPlaceholder: 'Search portfolio fits...',
    sortOptions: {
      score: 'Score',
      updatedAt: 'Last updated',
      createdAt: 'Created date',
    },
    filterOptions: {
      brand: 'Brand',
      status: 'Status',
      fit: 'Fit',
      campaign: 'Campaign',
    },
  },
  fr: {
    title: 'Portfolio fits',
    breadcrumb: 'Portfolio / Fits',
    searchPlaceholder: 'Rechercher des portfolio fits...',
    sortOptions: {
      score: 'Score',
      updatedAt: 'Derniere maj',
      createdAt: 'Date creation',
    },
    filterOptions: {
      brand: 'Marque',
      status: 'Statut',
      fit: 'Fit',
      campaign: 'Campagne',
    },
  },
} as const;

const PortfolioFitGridCard = memo(({ fitId, onOpen }: { fitId: string; onOpen: (fitId: string) => void }) => {
  const handleClick = useCallback(() => onOpen(fitId), [fitId, onOpen]);
  return <EntityView type="PortfolioFit" entityId={fitId} variant="card" onClick={handleClick} />;
});
PortfolioFitGridCard.displayName = 'PortfolioFitGridCard';

interface PortfolioFitsGridProps {
  initialFilters?: Partial<PortfolioFitFilters>;
  lockedFilters?: Partial<PortfolioFitFilters>;
  scopedIds?: string[] | null;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  renderCustomToolbarHeader?: () => React.ReactNode;
  disableManualRefresh?: boolean;
  onManualRefresh?: () => void | Promise<void>;
  viewModeVisual?: InventoryVisualMode;
  onViewModeVisualChange?: (mode: InventoryVisualMode) => void;
}

const PortfolioFitsGrid = memo(({
  initialFilters,
  lockedFilters,
  scopedIds,
  assetLedgerTitle,
  assetLedgerBreadcrumb,
  renderCustomToolbarHeader,
  disableManualRefresh,
  onManualRefresh,
  viewModeVisual,
  onViewModeVisualChange,
}: PortfolioFitsGridProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const [localViewModeVisual, setLocalViewModeVisual] = useState<InventoryVisualMode>('grid');
  const resolvedViewModeVisual = viewModeVisual ?? localViewModeVisual;

  const handleViewModeVisualChange = useCallback((mode: InventoryVisualMode) => {
    onViewModeVisualChange?.(mode);
    if (!onViewModeVisualChange) {
      setLocalViewModeVisual(mode);
    }
  }, [onViewModeVisualChange]);

  const effectiveInitialFilters = useMemo<Partial<PortfolioFitFilters>>(
    () =>
      initialFilters === undefined
        ? {
            sortBy: 'score',
            sortDir: 'desc',
          }
        : {
            ...initialFilters,
          },
    [initialFilters]
  );

  const sortOptions = useMemo(
    () => [
      { label: t.sortOptions.score, field: 'score' },
      { label: t.sortOptions.updatedAt, field: 'updatedAt' },
      { label: t.sortOptions.createdAt, field: 'createdAt' },
    ],
    [t.sortOptions.createdAt, t.sortOptions.score, t.sortOptions.updatedAt]
  );

  const filterOptions = useMemo(
    () => [
      { key: 'brandName', label: t.filterOptions.brand, icon: <Building2 size={16} strokeWidth={1.9} /> },
      { key: 'status', label: t.filterOptions.status, icon: <CheckCircle2 size={16} strokeWidth={1.9} /> },
      { key: 'score', label: t.filterOptions.fit, icon: <Gauge size={16} strokeWidth={1.9} /> },
      { key: 'campaignId', label: t.filterOptions.campaign, icon: <Target size={16} strokeWidth={1.9} /> },
    ],
    [t.filterOptions.brand, t.filterOptions.campaign, t.filterOptions.fit, t.filterOptions.status]
  );

  const handleOpenPortfolioFit = useCallback((portfolioFitId: string) => {
    if (!portfolioFitId) return;
    void kernel.current?.focusEntityUI('PortfolioFit', portfolioFitId);
  }, [kernel]);

  // Was `!== 'DISMISSED'`, the exact opposite of the lead grid, so a scored rejection vanished
  // here while its equivalent stayed visible five files away. DISMISSED is a RESULT and stays;
  // only a row a person removed is hidden.
  const hideDismissedPortfolioFits = useCallback(
    (fit: PortfolioFitDTO) => !isHumanRemoved(fit?.status),
    [],
  );

  const canShowViewModeToggle = supportsInventoryViewModeToggle('PortfolioFit');
  const composedToolbarHeader = useCallback(() => {
    if (!canShowViewModeToggle && !renderCustomToolbarHeader) return null;
    return (
      <>
        {canShowViewModeToggle && (
          <ViewModeGraphicToggle
            mode={resolvedViewModeVisual}
            onModeChange={handleViewModeVisualChange}
            className={renderCustomToolbarHeader ? 'mr-1' : undefined}
          />
        )}
        {renderCustomToolbarHeader?.()}
      </>
    );
  }, [
    canShowViewModeToggle,
    handleViewModeVisualChange,
    renderCustomToolbarHeader,
    resolvedViewModeVisual,
  ]);
  const effectiveToolbarHeader =
    canShowViewModeToggle || renderCustomToolbarHeader ? composedToolbarHeader : undefined;

  const renderPortfolioFitCard = useCallback(
    (fit: { id: string }) => <PortfolioFitGridCard key={fit.id} fitId={fit.id} onOpen={handleOpenPortfolioFit} />,
    [handleOpenPortfolioFit]
  );

  return (
    <Grid
      entityType="PortfolioFit"
      pageSize={24}
      initialFilters={effectiveInitialFilters}
      lockedFilters={lockedFilters}
      scopedIds={scopedIds}
      disableManualRefresh={disableManualRefresh}
      onManualRefresh={onManualRefresh}
      sortOptions={sortOptions}
      filterOptions={filterOptions}
      assetLedgerTitle={assetLedgerTitle || t.title}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb || t.breadcrumb}
      searchPlaceholder={t.searchPlaceholder}
      renderToolbarExtras={effectiveToolbarHeader}
      gridClass="grid-cols-[repeat(auto-fit,minmax(150px,1fr))]"
      itemPredicate={hideDismissedPortfolioFits}
      renderCard={renderPortfolioFitCard}
    />
  );
});

PortfolioFitsGrid.displayName = 'PortfolioFitsGrid';
export { PortfolioFitsGrid };
